import { PageLayout } from '@/components/layouts/PageLayout.tsx';
import React from 'react';
import { useMountEffect } from '@/hooks/useMountEffect.ts';
import { useGetPermissionsQuery, useLoginMutation } from '@/features/auth/auth.api.ts';
import { Navigate, useLocation } from 'react-router';
import { cn } from '@/utils/style.ts';
import { Spin } from 'antd';
import { PageTitle } from '@/components/PageTitle.tsx';
import { AppName } from '@/types/misc.def.ts';

import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store.ts';

export const LoginPage: React.FC = () => {
  const location = useLocation();
  const [login, loginResult] = useLoginMutation();
  const user = useSelector((state: RootState) => state.auth.user);
  const permissions = useSelector((state: RootState) => state.auth.permissions);

  const permissionsQueryResult = useGetPermissionsQuery(undefined, {
    skip: !user?.token,
  });

  const isLoading =
    loginResult.isLoading || (!!user && !permissions && !permissionsQueryResult.isError);

  useMountEffect(() => {
    if (!user) {
      void login({ envName: import.meta.env.VITE_IR_PORTAL_ENV });
    }
  });

  if (loginResult.isError) {
    throw new Error('Failed to login.');
  }

  if (permissionsQueryResult.isError) {
    throw new Error('Failed to fetch permissions.');
  }

  if (user && permissions) {
    return <Navigate to={location.state?.from || '/'} replace />;
  }

  return (
    <PageLayout className="h-full flex flex-col items-center justify-center">
      <PageTitle value="Login" />
      <h1 className="text-3xl font-bold mb-4">{AppName}</h1>
      <div
        className={cn('flex flex-col items-center justify-center collapse *:collapse', {
          '*:visible': isLoading,
        })}
      >
        <Spin size="large" />
        <p className="text-xl">Verifying your credential</p>
      </div>
    </PageLayout>
  );
};
